/**
 * CoreStudioMark - Core Carousel Mark (Symbol Only)
 *
 * Two chevron shapes + center diamond forming the Core Carousel geometric mark.
 * Paths extracted from the Figma design (Group 1.svg node 5:73).
 * Uses gradient fill by default, with optional currentColor support.
 *
 * Usage:
 *   <CoreStudioMark size={40} />
 *   <CoreStudioMark size={32} className="text-primary" useCurrentColor />
 */

import React from 'react';

interface CoreStudioMarkProps {
  size?: number;
  className?: string;
  /** If true, fills with currentColor instead of the brand gradient */
  useCurrentColor?: boolean;
}

const CoreStudioMark: React.FC<CoreStudioMarkProps> = ({
  size = 40,
  className = '',
  useCurrentColor = false,
}) => {
  const uid = React.useId().replace(/:/g, '');
  const leftFillId = `ccLeftFill_${uid}`;
  const rightFillId = `ccRightFill_${uid}`;
  const diamondFillId = `ccDiamondFill_${uid}`;
  const leftStrokeId = `ccLeftStroke_${uid}`;
  const rightStrokeId = `ccRightStroke_${uid}`;
  const diamondStrokeId = `ccDiamondStroke_${uid}`;

  const leftFill = useCurrentColor ? 'currentColor' : `url(#${leftFillId})`;
  const rightFill = useCurrentColor ? 'currentColor' : `url(#${rightFillId})`;
  const diamondFill = useCurrentColor ? 'currentColor' : `url(#${diamondFillId})`;
  const leftStroke = useCurrentColor ? undefined : `url(#${leftStrokeId})`;
  const rightStroke = useCurrentColor ? undefined : `url(#${rightStrokeId})`;
  const diamondStroke = useCurrentColor ? undefined : `url(#${diamondStrokeId})`;

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 160 160"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-label="Core Carousel"
      role="img"
    >
      {!useCurrentColor && (
        <defs>
          <linearGradient id={leftFillId} x1="39.3593" y1="62.8219" x2="39.3593" y2="160.765" gradientUnits="userSpaceOnUse">
            <stop stopColor="#3B3BF9" />
            <stop offset="1" stopColor="#5656FF" />
          </linearGradient>
          <linearGradient id={rightFillId} x1="132.583" y1="62.8219" x2="132.583" y2="160.765" gradientUnits="userSpaceOnUse">
            <stop stopColor="#3B3BF9" />
            <stop offset="1" stopColor="#5656FF" />
          </linearGradient>
          <linearGradient id={diamondFillId} x1="82.9775" y1="77.3896" x2="99.6412" y2="94.0533" gradientUnits="userSpaceOnUse">
            <stop stopColor="#3B3BF9" />
            <stop offset="1" stopColor="#5656FF" />
          </linearGradient>
          <linearGradient id={leftStrokeId} x1="39.3537" y1="0" x2="39.3537" y2="160.765" gradientUnits="userSpaceOnUse">
            <stop stopColor="#849CFF" />
            <stop offset="1" stopColor="white" stopOpacity="0" />
          </linearGradient>
          <linearGradient id={rightStrokeId} x1="132.577" y1="0" x2="132.577" y2="160.765" gradientUnits="userSpaceOnUse">
            <stop stopColor="#849CFF" />
            <stop offset="1" stopColor="white" stopOpacity="0" />
          </linearGradient>
          <linearGradient id={diamondStrokeId} x1="72.2873" y1="66.7033" x2="99.6392" y2="94.0552" gradientUnits="userSpaceOnUse">
            <stop stopColor="#849CFF" />
            <stop offset="1" stopColor="white" stopOpacity="0" />
          </linearGradient>
        </defs>
      )}
      <g transform="translate(80 80) scale(0.92) translate(-86 -80.5)">
        {/* Left chevron */}
        <path d="M53.0249 107.727L77.519 132.221C79.1035 133.806 79.1035 136.37 77.519 137.95L55.8917 159.577C54.3071 161.161 51.7427 161.161 50.1631 159.577L25.669 135.083L1.18472 110.594C-0.394908 109.009 -0.394908 106.445 1.18472 104.865L20.2719 85.7779C20.5303 85.5195 20.7644 85.227 20.9399 84.9052C22.9827 81.2535 22.968 78.0601 20.662 75.3835L1.18472 55.8917C-0.394908 54.3071 -0.394908 51.7427 1.18472 50.1631L25.6641 25.669L50.1631 1.18472C51.7476 -0.394908 54.312 -0.394908 55.8917 1.18472L77.519 22.8023C79.1035 24.3868 79.1035 26.9512 77.519 28.5309L31.0612 74.9886C30.8028 75.247 30.5737 75.5395 30.3933 75.8564C28.3505 79.4984 28.3797 82.6918 30.6712 85.383L53.0249 107.727Z" fill={leftFill} />
        {leftStroke && (
          <path d="M50.6445 1.66699C51.9631 0.352901 54.0966 0.354548 55.4092 1.66699L77.0361 23.2852C78.3545 24.6035 78.3536 26.7354 77.0371 28.0479H77.0361L30.5781 74.5059C30.2811 74.8029 30.0133 75.1435 29.7998 75.5186L29.7979 75.5225C28.7363 77.415 28.1824 79.2457 28.2246 80.9932C28.2672 82.7533 28.9139 84.3717 30.1514 85.8252L30.1689 85.8467L30.1885 85.8662L52.542 108.21L77.0361 132.704C78.3543 134.022 78.3534 136.153 77.0371 137.466L77.0361 137.467L55.4092 159.095C54.0908 160.413 51.9589 160.411 50.6465 159.095H50.6455L26.1514 134.601L1.66699 110.111H1.66797C0.353855 108.793 0.354575 106.66 1.66699 105.348L20.7549 86.2607C21.0481 85.9676 21.3256 85.6237 21.5391 85.2324L21.5381 85.2314C22.5965 83.3376 23.1544 81.5078 23.1143 79.7627C23.0737 78.002 22.4257 76.3859 21.1787 74.9385L21.1631 74.9189L21.1445 74.9014L1.66797 55.4092H1.66699C0.353396 54.0906 0.354705 51.9579 1.66699 50.6455H1.66797L26.1465 26.1514L50.6455 1.66797L50.6445 1.66699Z" stroke={leftStroke} strokeOpacity="0.7" strokeWidth="1.365" fill="none" />
        )}
        {/* Right chevron */}
        <path d="M151.265 85.383L170.742 104.86C172.326 106.445 172.326 109.009 170.742 110.589L146.262 135.083L121.768 159.577C120.184 161.161 117.619 161.161 116.04 159.577L94.4125 137.95C92.828 136.365 92.828 133.801 94.4125 132.221L118.907 107.727L140.87 85.773C141.129 85.5146 141.363 85.2172 141.543 84.9003C143.586 81.2486 143.557 78.0553 141.265 75.3787L94.4174 28.5309C92.8329 26.9464 92.8329 24.3819 94.4174 22.8023L116.04 1.18472C117.624 -0.394908 120.189 -0.394908 121.768 1.18472L146.262 25.6641L170.742 50.1582C172.321 51.7427 172.321 54.3023 170.742 55.8868L151.65 74.9886C151.391 75.247 151.162 75.5395 150.982 75.8564C148.939 79.4984 148.954 82.6918 151.26 85.383H151.265Z" fill={rightFill} />
        {rightStroke && (
          <path d="M116.522 1.66797C117.84 0.353782 119.973 0.354645 121.286 1.66699V1.66797L145.78 26.1465L170.258 50.6387V50.6396C171.572 51.9575 171.573 54.0861 170.259 55.4043L151.167 74.5059C150.87 74.803 150.602 75.1434 150.388 75.5186L150.386 75.5225C149.325 77.4141 148.767 79.2445 148.807 80.9922C148.848 82.7534 149.496 84.3729 150.742 85.8271L150.946 86.0654H150.982L170.259 105.343C171.578 106.661 171.577 108.793 170.26 110.105L170.259 110.106L145.78 134.601L121.286 159.095C119.967 160.413 117.835 160.411 116.523 159.095H116.522L94.895 137.467C93.5768 136.148 93.5776 134.017 94.894 132.704H94.895L119.389 108.21L141.353 86.2559C141.649 85.9596 141.922 85.6142 142.136 85.2383L142.139 85.2334C143.2 83.3367 143.754 81.5044 143.711 79.7578C143.669 77.9978 143.022 76.3815 141.784 74.9346L141.766 74.915L141.748 74.8965L94.8999 28.0479C93.5819 26.7296 93.5829 24.5986 94.8989 23.2861L94.8999 23.2852L116.521 1.66699L116.522 1.66797Z" stroke={rightStroke} strokeOpacity="0.7" strokeWidth="1.365" fill="none" />
        )}
        {/* Center diamond */}
        <path d="M83.519 55.4715L61.0556 77.935C59.7057 79.2849 59.7056 81.4736 61.0556 82.8235L83.519 105.287C84.8689 106.637 87.0576 106.637 88.4075 105.287L110.871 82.8235C112.221 81.4736 112.221 79.2849 110.871 77.935L88.4075 55.4715C87.0576 54.1216 84.8689 54.1216 83.519 55.4715Z" fill={diamondFill} />
        {diamondStroke && (
          <path d="M61.5385 78.4174L84.0016 55.9544C85.0849 54.871 86.8418 54.871 87.9252 55.9544L110.388 78.4174C111.472 79.5008 111.472 81.2577 110.388 82.3411L87.9252 104.804C86.8418 105.887 85.0849 105.887 84.0016 104.804L61.5385 82.3411C60.4552 81.2577 60.4551 79.5008 61.5385 78.4174Z" stroke={diamondStroke} strokeOpacity="0.7" strokeWidth="1.365" fill="none" />
        )}
      </g>
    </svg>
  );
};

export default CoreStudioMark;
